import APIs from '../../../api'
import { apiClient } from '../../../api/client'

const listManufacturers = ({
  page = 1,
  limit = 10,
  search = '',
  sortBy,
  direction,
  ...rest
} = {}) => {
  const params = { page, limit }

  if (search) params.search = search

  if (sortBy && direction) {
    params.sortBy = sortBy
    params.direction = direction
  }

  Object.assign(params, rest)

  return apiClient.get(APIs.MANUFACTURERS.list, { params })
}

const listSensors = ({ page = 1, limit = 10, search = '', sortBy, direction, ...rest } = {}) => {
  const params = { page, limit }

  if (search) params.search = search

  if (sortBy && direction) {
    params.sortBy = sortBy
    params.direction = direction
  }

  Object.assign(params, rest)

  return apiClient.get(APIs.SENSORS.list, { params })
}

const listAlarmProfiles = ({
  page = 1,
  limit = 10,
  search = '',
  sortBy,
  direction,
  ...rest
} = {}) => {
  const params = { page, limit }

  if (search) params.name = search

  if (sortBy && direction) {
    params.sortBy = sortBy
    params.direction = direction
  }

  Object.assign(params, rest)

  return apiClient.get(APIs.ALARM_PROFILES.list, { params })
}

const createDevice = (payload, file) => {
  // payload is a plain object with the id-shaped fields coerced to numbers
  // and `sensorIds` as an array of numbers; `file` is the optional File
  // picked from FileUpload (or null). The backend accepts the file as
  // multipart/form-data, so we send everything via a FormData body.
  const body = new FormData()
  Object.entries(payload).forEach(([k, v]) => {
    if (v == null) return
    if (Array.isArray(v)) body.append(k, v.join(','))
    else body.append(k, String(v))
  })
  if (file) body.append('calibrationCertificate', file)
  return apiClient.post(APIs.DEVICES.create, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// GET /devices — backend accepts page/limit + optional filters/search/orderBy/order.
// PaginationFooter sends `per_page` + `current_page`; map to the API's `limit` + `page`.
// `status` is an optional device-status filter (e.g. "ACTIVE") forwarded
// straight to the backend — used by the V-Tag couple/swap dropdown to
// hide retired or decommissioned hardware from the picker.
const listDevicesApi = (params = {}) => {
  const { per_page, current_page, search, status, orderBy, order, filterFields, filterInputs } =
    params || {}

  const query = { page: current_page, limit: per_page }
  if (search) query.search = search
  if (status) query.status = status
  filterFields?.forEach((field, index) => {
    const value = filterInputs?.[index]
    if (value != null && value !== '') {
      query[field] = value
    }
  })
  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order

  return apiClient.get(APIs.DEVICES.list, { params: query })
}

// GET /devices/{uuid} — single device detail. Response body is wrapped
// in `{ success, message, data }`; the device object lives at `data`.
const getDeviceByUuid = (uuid) => apiClient.get(APIs.DEVICES.byUuid(uuid))

// PUT /devices/{uuid} — partial device update. The backend accepts every
// device field via multipart/form-data (same body shape as `createDevice`),
// so we send just the `status` field — the rest are left untouched. The
// Devices table's "Inactive" row action fires this with `status: 'INACTIVE'`.
const updateDeviceStatusApi = (uuid, status) => {
  const body = new FormData()
  body.append('status', status)
  return apiClient.put(APIs.DEVICES.byUuid(uuid), body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// PUT /devices/{uuid} — full device update via multipart/form-data.
// Mirrors `createDevice`'s FormData shape so the backend accepts the same
// field set on the same endpoint. `file` is the optional new calibration
// certificate; the existing file is preserved server-side when no file
// is appended. Used by the row-level Edit action.
const updateDeviceApi = (uuid, payload, file) => {
  const body = new FormData()
  Object.entries(payload).forEach(([k, v]) => {
    if (v == null) return
    if (Array.isArray(v)) body.append(k, v.join(','))
    else body.append(k, String(v))
  })
  if (file) body.append('calibrationCertificate', file)
  return apiClient.put(APIs.DEVICES.byUuid(uuid), body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// GET /devices/unattached — devices not currently coupled to any V-Tag.
// Backed by the same backend DTO contract as `listDevicesApi` (page/limit
// + optional `status`/`manufacturerId` filters), so the call site can
// reuse the same param shape. Used by the couple/swap modal so the user
// can only pick a free device for the new coupling.
const listUnattachedDevices = (params = {}) => {
  const { per_page, current_page, page, limit, search, status, manufacturerId } = params || {}
  const query = { page: current_page ?? page, limit: per_page ?? limit }
  if (search) query.search = search
  if (status) query.status = status
  if (manufacturerId) query.manufacturerId = manufacturerId
  return apiClient.get(APIs.DEVICES.unattached, { params: query })
}

// GET /asset-tags/swap-history/{uuid} — paginated coupling/swap history
// for a single asset tag. The backend wraps the list in
// `{ success, message, data: [...], meta: { total, page, limit, totalPages } }`,
// which lines up with the shared PaginationFooter reader
// (`response.data.data` → array, `response.data.meta.total` → total).
// `per_page` / `current_page` come from PaginationFooter's listCall mode
// and are remapped to `limit` / `page` here. The free-text `search` is
// forwarded verbatim for server-side filtering.
//
// When `uuid` is missing (parent device hasn't loaded yet, or the device
// has no associated asset tag) we short-circuit with an empty success
// response so the URL never contains the literal string "undefined".
// Without this guard `APIs.ASSET_TAGS.swapHistory(undefined)` resolves to
// `/asset-tags/swap-history/undefined`, which is exactly the noisy call
// we're trying to suppress.

// GET /devices/export — streams a CSV of the filtered device set. The
// backend honours the same filters as `listDevicesApi` (manufacturerId,
// assetTagId, calibrationExpiryDateFrom/To, search) but ignores
// `page`/`limit` — pagination is dropped so the export contains every
// matching row. `columns` is an array; axios serializes it as repeated
// `columns=…&columns=…` query params which matches the documented curl.
// `responseType: 'blob'` keeps the CSV payload opaque so axios doesn't
// try to JSON-parse a `text/csv` body.
const exportDevicesCsv = async ({ columns, orderBy, order, ...filters } = {}) => {
  const params = {}
  if (columns?.length) params.columns = columns
  if (orderBy) params.orderBy = orderBy
  if (order) params.order = order
  // Forward every remaining filter key as-is. Empty strings are dropped
  // server-side, so sending `''` for unset filters is harmless — but we
  // strip them here to keep the URL clean.
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v != null) params[k] = v
  })
  const response = await apiClient.get(APIs.DEVICES.export, {
    params,
    responseType: 'blob',
  })
  return response.data
}
const getAssetTagSwapHistory = (params = {}) => {
  const { uuid, per_page, current_page, search, orderBy, order } = params || {}
  if (!uuid) {
    return Promise.resolve({
      data: {
        success: true,
        message: '',
        data: [],
        meta: { total: 0, page: 1, limit: per_page ?? 10, totalPages: 1 },
      },
    })
  }
  const query = { page: current_page, limit: per_page }
  if (search) query.search = search
  if (orderBy) query.orderBy = orderBy
  if (order) query.order = order
  return apiClient.get(APIs.ASSET_TAGS.swapHistory(uuid), { params: query })
}

// GET /alarm-profiles/{uuid} — single alarm profile template. Response is
// wrapped in `{ success, message, data }`; the profile object (carrying
// `name`, `profiles[]`, `notificationRules[]`, `recipients[]`, etc.) lives
// at `data`. Used by the Device Detail page to render the Alarm Profiles
// section from the device's `alarmProfileTemplates[].uuid`.
const getAlarmProfileByUuid = (uuid) => {
  if (!uuid) {
    return Promise.resolve({ data: { success: true, message: '', data: null } })
  }
  return apiClient.get(APIs.ALARM_PROFILES.byUuid(uuid))
}

// GET /device-datalogs/filter?serialNumber=…&sensorSlug=…&startDate=…&endDate=…
// Reads a single sensor's last-N-hours window for the Device Detail chart.
// The response is wrapped in `{ success, message, data: DeviceDatalog[] }`,
// where each entry carries `sensorValue` (string) and `logDateTime` (ISO).
// The caller maps the array into `{ time, v }` points for the SVG chart.
// `startDate`/`endDate` are ISO strings; pass both for a bounded window.
const filterDeviceDatalogsApi = ({ serialNumber, sensorSlug, startDate, endDate } = {}) => {
  return apiClient.get(APIs.DEVICE_DATALOGS.filter, {
    params: { serialNumber, sensorSlug, startDate, endDate },
  })
}

export {
  listManufacturers,
  listSensors,
  listAlarmProfiles,
  createDevice,
  listDevicesApi,
  listUnattachedDevices,
  exportDevicesCsv,
  getDeviceByUuid,
  updateDeviceStatusApi,
  updateDeviceApi,
  getAssetTagSwapHistory,
  filterDeviceDatalogsApi,
  getAlarmProfileByUuid,
}
